import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Swords, X, UserPlus } from "lucide-react";
import { InfoTip } from "./InfoTip";

export default function CombatTrackerPanel({ gameId }) {
  const tokens = useGameStore((s) => s.tokens);
  const combat = useGameStore((s) => s.combat);
  const [initiatives, setInitiatives] = useState({});
  const [addTokenId, setAddTokenId] = useState("");
  const [addInitiative, setAddInitiative] = useState("");
  // Roster for the pre-fight setup screen. Defaults to every PC/NPC token on the
  // scene, but the GM can manually remove any of them (e.g. a fled/dead NPC) or
  // add any other token on the map (including hazards) before hitting Start Combat.
  const [rosterIds, setRosterIds] = useState(() => tokens.filter((t) => t.token_type === "pc" || t.token_type === "npc").map((t) => t.id));

  useEffect(() => {
    if (combat.active) return;
    setRosterIds((prev) => {
      const validIds = new Set(tokens.map((t) => t.id));
      const kept = prev.filter((id) => validIds.has(id));
      const defaults = tokens.filter((t) => (t.token_type === "pc" || t.token_type === "npc") && !kept.includes(t.id)).map((t) => t.id);
      return [...kept, ...defaults];
    });
  }, [tokens, combat.active]);

  const combatants = rosterIds.map((id) => tokens.find((t) => t.id === id)).filter(Boolean);
  const rosterAddable = tokens.filter((t) => !rosterIds.includes(t.id));

  const setInit = (tokenId, val) => setInitiatives({ ...initiatives, [tokenId]: parseInt(val, 10) || 0 });

  const removeFromRoster = (tokenId) => setRosterIds((prev) => prev.filter((id) => id !== tokenId));
  const addToRoster = (tokenId) => setRosterIds((prev) => [...prev, tokenId]);

  const startCombat = async () => {
    const order = combatants.map((t) => ({ token_id: t.id, name: t.name, initiative: initiatives[t.id] || 0 }));
    await api.post(`/games/${gameId}/combat/start`, { order });
  };

  // Keeps whoever currently has the turn pointed at correctly even after the order array shifts
  // from a mid-fight add/remove (rather than trusting a now-stale numeric turn_index).
  const patchCombatOrder = async (newOrder) => {
    const currentTokenId = combat.order[combat.turn_index]?.token_id;
    let turn_index = newOrder.findIndex((o) => o.token_id === currentTokenId);
    if (turn_index === -1) turn_index = Math.min(combat.turn_index, Math.max(0, newOrder.length - 1));
    await api.patch(`/games/${gameId}/combat`, { order: newOrder, turn_index });
  };

  const removeCombatant = (tokenId) => patchCombatOrder(combat.order.filter((o) => o.token_id !== tokenId));

  const addableTokens = tokens.filter((t) => (t.token_type === "pc" || t.token_type === "npc") && !combat.order.some((o) => o.token_id === t.id));

  const addCombatant = () => {
    const t = addableTokens.find((tok) => tok.id === addTokenId);
    if (!t) return;
    const entry = { token_id: t.id, name: t.name, initiative: parseInt(addInitiative, 10) || 0 };
    const newOrder = [...combat.order, entry].sort((a, b) => (b.initiative || 0) - (a.initiative || 0));
    patchCombatOrder(newOrder);
    setAddTokenId("");
    setAddInitiative("");
  };

  if (combat.active) {
    return (
      <div className="space-y-2" data-testid="combat-tracker-active">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Round {combat.round}</p>
        <div className="space-y-1">
          {combat.order.map((o, i) => (
            <div key={o.token_id} className={`flex items-center justify-between text-sm px-2 py-1.5 rounded-lg ${i === combat.turn_index ? "bg-[#D90429]/20 border border-[#D90429]/40" : "bg-black/20"}`}>
              <span className="truncate">{o.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-[#D4AF37] text-xs">{o.initiative}</span>
                <button data-testid={`remove-combatant-${o.token_id}`} onClick={() => removeCombatant(o.token_id)} title="Remove from initiative" className="text-[#f1e0c5]/30 hover:text-[#D90429]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        {addableTokens.length > 0 && (
          <div className="flex items-center gap-1.5 pt-1.5 border-t border-white/10">
            <Select value={addTokenId} onValueChange={setAddTokenId}>
              <SelectTrigger data-testid="combat-add-token-select" className="bg-black/30 border-white/10 h-7 text-[10px] flex-1"><SelectValue placeholder="Add a token…" /></SelectTrigger>
              <SelectContent>{addableTokens.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="combat-add-initiative-input" type="number" placeholder="Init" value={addInitiative} onChange={(e) => setAddInitiative(e.target.value)} className="bg-black/30 border-white/10 h-7 text-xs w-16" />
            <Button data-testid="combat-add-token-button" size="sm" variant="secondary" className="rounded-full h-7 text-[10px] gap-1 shrink-0" onClick={addCombatant} disabled={!addTokenId}>
              <UserPlus className="w-3 h-3" /> Add
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="combat-tracker-setup">
      <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5">Set Initiative &amp; Start Combat <InfoTip topic="combat_tracker" /></p>
      {combatants.map((t) => (
        <div key={t.id} className="flex items-center justify-between gap-2">
          <span className="text-sm truncate flex-1">{t.name}</span>
          <Input
            data-testid={`initiative-input-${t.id}`}
            type="number"
            placeholder="0"
            className="w-16 h-7 bg-black/30 border-white/10 text-xs"
            onChange={(e) => setInit(t.id, e.target.value)}
          />
          <button data-testid={`setup-remove-token-${t.id}`} onClick={() => removeFromRoster(t.id)} title="Remove from roster" className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      {combatants.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No tokens in the roster. Add one below.</p>}
      {rosterAddable.length > 0 && (
        <div className="flex items-center gap-1.5 pt-1.5 border-t border-white/10">
          <Select value={addTokenId} onValueChange={setAddTokenId}>
            <SelectTrigger data-testid="setup-add-token-select" className="bg-black/30 border-white/10 h-7 text-[10px] flex-1"><SelectValue placeholder="Add a token…" /></SelectTrigger>
            <SelectContent>{rosterAddable.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button
            data-testid="setup-add-token-button"
            size="sm"
            variant="secondary"
            className="rounded-full h-7 text-[10px] gap-1 shrink-0"
            onClick={() => {
              if (!addTokenId) return;
              addToRoster(addTokenId);
              setAddTokenId("");
            }}
            disabled={!addTokenId}
          >
            <UserPlus className="w-3 h-3" /> Add
          </Button>
        </div>
      )}
      <Button data-testid="start-combat-button" onClick={startCombat} disabled={combatants.length === 0} size="sm" className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
        <Swords className="w-3.5 h-3.5" /> Start Combat
      </Button>
    </div>
  );
}
